import * as Yup from 'yup';

import type { YesNoBoolean } from '@customTypes/form';
import { isAdultDOB, isMinorDOB } from '@utils/dates';
import { AdultDOBValidator, DOBValidator } from '@validators/subject';

import type { FamilyHistory, FamilyHistoryItem } from '@customTypes';

// ============================================================================
// Address Schemas (for WizardState - simpler than form validation)
// ============================================================================

/**
 * Schema for optional address fields in WizardState.
 * These are simpler than form validation - just type checking, not business rules.
 */
const wizardAddressSchema = Yup.object({
  street: Yup.string().optional(),
  street2: Yup.string().optional(),
  city: Yup.string().optional(),
  state: Yup.string().optional(),
  zip: Yup.string().optional(),
}).optional();

/**
 * Schema for AddressPayload in OrderState.
 * Uses different field names than wizard addresses.
 */
const addressPayloadSchema = Yup.object({
  street_address: Yup.string().optional(),
  street_address_2: Yup.string().optional(),
  city: Yup.string().optional(),
  region: Yup.string().optional(),
  postal_code: Yup.string().optional(),
  country: Yup.string().optional(),
}).optional();

// ============================================================================
// OrderState Schema
// ============================================================================

const contactInfoSchema = Yup.object({
  address: addressPayloadSchema,
  email: Yup.string().optional(),
  phone: Yup.string().optional(),
  fax: Yup.string().optional(),
});

const subjectNameSchema = Yup.object({
  first: Yup.string().optional(),
  middle: Yup.string().optional(),
  last: Yup.string().optional(),
  credentials: Yup.string().optional(),
});

const subjectDemographicsSchema = Yup.object({
  dob: DOBValidator.optional(),
  sex: Yup.string().optional(),
});

const subjectSchema = Yup.object({
  name: subjectNameSchema.required(),
  contact_info: Yup.array().of(contactInfoSchema).required(),
  demographics: subjectDemographicsSchema.required(),
});

const productSchema = Yup.object({
  product_id: Yup.string().required(),
  name: Yup.string().required(),
  product_family_name: Yup.string().nullable().optional(),
  product_test_name: Yup.string().nullable().optional(),
  product_test_type: Yup.string().nullable().optional(),
  product_test_subclass: Yup.string().nullable().optional(),
  description: Yup.string().optional(),
  requires_family_history: Yup.boolean().optional(),
});

/**
 * Schema for a single family history response entry.
 */
const familyHistoryResponseSchema = Yup.object<FamilyHistoryItem>({
  question_text: Yup.string().required(),
  response: Yup.string().oneOf<YesNoBoolean>(['YES', 'NO']).required(),
});

/**
 * Validates family_history as a Record<string, {question_text: string, response: 'YES' | 'NO'}>.
 * Uses Yup.lazy to dynamically build schema based on input keys.
 */
const familyHistorySchema = Yup.lazy(input => {
  // Handle null/undefined - return required object schema
  if (input === null || input === undefined) {
    return Yup.object<FamilyHistory>().required();
  }

  // Build object schema with keys from input and values of familyHistoryResponseSchema
  const shape = Object.keys(input).reduce(
    (newShape, key) => {
      newShape[key] = familyHistoryResponseSchema;
      return newShape;
    },
    {} as Record<string, typeof familyHistoryResponseSchema>,
  );

  return Yup.object<FamilyHistory>(shape).required();
});

/**
 * Validates product_information as a flexible object structure.
 * Since it's optional and has nested optional fields, we just validate it's an object if present.
 */
const productInformationSchema = Yup.object().optional();

const guardianSchema = Yup.object({
  first_name: Yup.string().required(),
  last_name: Yup.string().required(),
  dob: AdultDOBValidator.required(),
  email: Yup.string().email().required(),
  phone: Yup.string().required(),
  relationship: Yup.string().required(),
});

export const orderStateSchema = Yup.object({
  campaign_code: Yup.string().optional(),
  consents_granted: Yup.array().of(Yup.string()).required(),
  eligibilityChecked: Yup.boolean().required(),
  external_member_id: Yup.string().optional(),
  family_history: familyHistorySchema,
  guardian: guardianSchema.nullable().optional(),
  product_information: productInformationSchema,
  products: Yup.array().of(productSchema).required(),
  // Optional so drafts saved before product selection existed still validate.
  selected_product_ids: Yup.array().of(Yup.string().required()).optional(),
  subject: subjectSchema.required(),
  is_rerequisition: Yup.boolean().optional(),
})
  .required()
  .test('guardian-required-for-minors', (value, options) => {
    const dob = value?.subject?.demographics?.dob;
    const guardian = value?.guardian;

    if (isAdultDOB(dob) && guardian) {
      return options.createError({
        path: 'guardian',
        message: 'Guardian cannot be provided for adults.',
      });
    }

    if (isMinorDOB(dob) && !guardian) {
      return options.createError({
        path: 'guardian',
        message: 'Guardian information is required when the subject is under 18.',
      });
    }

    return true;
  });

// ============================================================================
// WizardState Schema
// ============================================================================

const consentsSchema = Yup.object({
  acceptGeneral: Yup.boolean().required(),
  acceptDNAVisit: Yup.boolean().required(),
  familyHistoryAttest: Yup.boolean().required(),
}).optional();

const confirmedBillingAddressSchema = Yup.object({
  useShippingForBilling: Yup.boolean().required(),
  address: wizardAddressSchema,
}).optional();

export const wizardStateSchema = Yup.object({
  currentStep: Yup.number().integer().min(0).required(),
  billingAddress: wizardAddressSchema,
  consents: consentsSchema,
  useShippingForBilling: Yup.boolean().required(),
  promoCode: Yup.string().optional(),
  confirmedBillingAddress: confirmedBillingAddressSchema,
}).required();
